import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Brain, Play, RefreshCw, CheckCircle, AlertCircle, Loader2, Database, SlidersHorizontal, ArrowRightLeft, BookOpen } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EmbeddingsManagementPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [staleInfo, setStaleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [tuning, setTuning] = useState({ ask_search_threshold: 55, duplicate_detection_threshold: 65, written_duplicate_threshold: 80 });
  const [tuningDraft, setTuningDraft] = useState({ ask_search_threshold: 55, duplicate_detection_threshold: 65, written_duplicate_threshold: 80 });
  const [savingTuning, setSavingTuning] = useState(false);
  const [tuningDirty, setTuningDirty] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const [showModelConfirm, setShowModelConfirm] = useState(false);
  const [pendingModel, setPendingModel] = useState(null);
  const [switchingModel, setSwitchingModel] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = token;
      const res = await axios.get(`${API}/api/admin-tools/embeddings/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch embedding status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const token = token;
      const res = await axios.get(`${API}/api/admin-tools/embeddings/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch progress:', err);
      return null;
    }
  }, []);

  const fetchStaleInfo = async () => {
    try {
      const token = token;
      const res = await axios.get(`${API}/api/admin-tools/embeddings/stale`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaleInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch stale info:', err);
    }
  };

  const refreshStale = async () => {
    setRefreshing(true);
    try {
      const token = token;
      const res = await axios.post(`${API}/api/admin-tools/embeddings/refresh-stale`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Refresh Complete", description: `${res.data.refreshed} embeddings updated` });
      fetchStatus();
      fetchStaleInfo();
    } catch (err) {
      toast({ title: "Error", description: "Failed to refresh stale embeddings", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchTuning = async () => {
    try {
      const token = token;
      const res = await axios.get(`${API}/api/admin-tools/search-tuning`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTuning(res.data);
      setTuningDraft(res.data);
    } catch (err) {
      console.error('Failed to fetch search tuning:', err);
    }
  };

  const saveTuning = async () => {
    setSavingTuning(true);
    try {
      const token = token;
      await axios.put(`${API}/api/admin-tools/search-tuning`, tuningDraft, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTuning({ ...tuningDraft });
      setTuningDirty(false);
      toast({ title: "Saved", description: "Search thresholds updated. Changes take effect immediately." });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to save", variant: "destructive" });
    } finally {
      setSavingTuning(false);
    }
  };

  const fetchModelInfo = async () => {
    try {
      const token = token;
      const res = await axios.get(`${API}/api/admin-tools/model/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModelInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch model info:', err);
    }
  };

  const handleModelSwitch = async () => {
    if (!pendingModel) return;
    setSwitchingModel(true);
    setShowModelConfirm(false);
    try {
      const token = token;
      const res = await axios.post(`${API}/api/admin-tools/model/switch`, { model_key: pendingModel }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Model Switched", description: res.data.message });
      setPolling(true);
      fetchModelInfo();
      fetchStatus();
      fetchProgress();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to switch model", variant: "destructive" });
    } finally {
      setSwitchingModel(false);
      setPendingModel(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchProgress();
    fetchStaleInfo();
    fetchTuning();
    fetchModelInfo();
  }, [fetchStatus, fetchProgress]);

  // Poll progress when a job is running
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      const prog = await fetchProgress();
      await fetchStatus();
      if (prog && prog.status !== 'running') {
        setPolling(false);
        if (prog.status === 'completed') {
          toast({ title: "Backfill Complete", description: `Processed ${prog.processed} questions` });
        } else if (prog.status === 'failed') {
          toast({ title: "Backfill Failed", description: "Check server logs for details", variant: "destructive" });
        }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, fetchProgress, fetchStatus, toast]);

  // Start polling if job is already running on page load
  useEffect(() => {
    if (progress && progress.status === 'running') {
      setPolling(true);
    }
  }, [progress]);

  const startBackfill = async () => {
    setStarting(true);
    try {
      const token = token;
      const res = await axios.post(`${API}/api/admin-tools/embeddings/backfill`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Backfill Started", description: `Processing ${res.data.total_to_process} questions...` });
      setPolling(true);
      await fetchProgress();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start backfill';
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setStarting(false);
    }
  };

  const isRunning = progress?.status === 'running';
  const coveragePercent = status?.coverage_percent || 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto" data-testid="embeddings-management-page">
        <Card className="mb-8 border-l-4 border-l-teal-600 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-teal-600/5 to-cyan-600/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-600 rounded-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl sm:text-3xl text-teal-700" data-testid="embeddings-title">
                  Semantic Embeddings
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  Manage pre-computed question embeddings for faster similarity detection
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-2xl font-bold text-gray-900" data-testid="total-questions">
                    {status?.total_approved_questions?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Total Questions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-2xl font-bold text-teal-600" data-testid="total-embedded">
                    {status?.total_embedded?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">With Embeddings</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-2xl font-bold text-amber-600" data-testid="gap-count">
                    {status?.gap?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Missing</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-5 pb-4 text-center">
                  <p className={`text-2xl font-bold ${coveragePercent >= 95 ? 'text-green-600' : coveragePercent >= 50 ? 'text-amber-600' : 'text-red-600'}`}
                     data-testid="coverage-percent">
                    {coveragePercent}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Coverage</p>
                </CardContent>
              </Card>
            </div>

            {/* Coverage Bar */}
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Embedding Coverage</span>
                  <span className="text-sm text-gray-500">{status?.total_embedded || 0} / {status?.total_approved_questions || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden" data-testid="coverage-bar">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      coveragePercent >= 95 ? 'bg-green-500' : coveragePercent >= 50 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(coveragePercent, 100)}%` }}
                  />
                </div>
                {coveragePercent >= 100 ? (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    All questions have embeddings. New questions are auto-embedded on approval.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">
                    {status?.gap || 0} questions need embeddings computed. Click "Compute Embeddings" below.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Active Job Progress */}
            {isRunning && progress && (
              <Card className="border-teal-200 bg-teal-50/50">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                    <span className="text-sm font-medium text-teal-700">Backfill in Progress</span>
                    <Badge variant="outline" className="text-teal-700 border-teal-300">
                      {progress.progress_percent}%
                    </Badge>
                  </div>
                  <div className="w-full bg-teal-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-teal-600 rounded-full transition-all duration-300"
                      style={{ width: `${progress.progress_percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-teal-600">
                    <span>{progress.processed} / {progress.total} processed</span>
                    {progress.errors > 0 && <span className="text-red-600">{progress.errors} errors</span>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={startBackfill}
                    disabled={starting || isRunning || (status?.gap === 0)}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="compute-embeddings-btn"
                  >
                    {starting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    {isRunning ? 'Backfill Running...' : status?.gap === 0 ? 'All Embedded' : `Compute Embeddings (${status?.gap || 0})`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { fetchStatus(); fetchProgress(); fetchStaleInfo(); }}
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {status?.gap === 0 && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    All questions are embedded. New questions are auto-embedded when approved.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Search Tuning */}
            <Card data-testid="search-tuning-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-violet-500" />
                  Search Tuning
                </CardTitle>
                <CardDescription>
                  Control how strict the similarity matching is for different contexts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Duplicate Detection Threshold */}
                <div data-testid="dup-threshold-section">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Oral Duplicate Detection Sensitivity</p>
                      <p className="text-xs text-gray-400">Used during oral question review and bulk import</p>
                    </div>
                    <span className="text-lg font-bold text-violet-600 tabular-nums w-16 text-right" data-testid="dup-threshold-value">
                      {tuningDraft.duplicate_detection_threshold}%
                    </span>
                  </div>
                  <Slider
                    value={[tuningDraft.duplicate_detection_threshold]}
                    onValueChange={([val]) => {
                      setTuningDraft(prev => ({ ...prev, duplicate_detection_threshold: val }));
                      setTuningDirty(true);
                    }}
                    min={30}
                    max={95}
                    step={1}
                    className="py-2"
                    data-testid="dup-threshold-slider"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>Loose (30%)</span>
                    <span>Balanced (65%)</span>
                    <span>Strict (95%)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Higher = only very similar questions flagged as duplicates. Lower = more questions flagged.
                  </p>
                </div>

                {/* Written Duplicate Detection Threshold */}
                <div className="pt-2 border-t" data-testid="written-dup-threshold-section">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Written Duplicate Detection Sensitivity</p>
                      <p className="text-xs text-gray-400">Used when checking duplicates on Written paper questions</p>
                    </div>
                    <span className="text-lg font-bold text-amber-600 tabular-nums w-16 text-right" data-testid="written-dup-threshold-value">
                      {tuningDraft.written_duplicate_threshold}%
                    </span>
                  </div>
                  <Slider
                    value={[tuningDraft.written_duplicate_threshold]}
                    onValueChange={([val]) => {
                      setTuningDraft(prev => ({ ...prev, written_duplicate_threshold: val }));
                      setTuningDirty(true);
                    }}
                    min={30}
                    max={95}
                    step={1}
                    className="py-2"
                    data-testid="written-dup-threshold-slider"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>Loose (30%)</span>
                    <span>Balanced (80%)</span>
                    <span>Strict (95%)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Written questions are typically longer and more distinct — a stricter default (80%) reduces false positives. Uses cached embeddings only for fast lookup.
                  </p>
                </div>

                {/* Ask Search Threshold */}
                <div className="pt-2 border-t" data-testid="ask-threshold-section">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Ask Search Sensitivity</p>
                      <p className="text-xs text-gray-400">Used on the Ask page for semantic search results</p>
                    </div>
                    <span className="text-lg font-bold text-teal-600 tabular-nums w-16 text-right" data-testid="ask-threshold-value">
                      {tuningDraft.ask_search_threshold}%
                    </span>
                  </div>
                  <Slider
                    value={[tuningDraft.ask_search_threshold]}
                    onValueChange={([val]) => {
                      setTuningDraft(prev => ({ ...prev, ask_search_threshold: val }));
                      setTuningDirty(true);
                    }}
                    min={30}
                    max={95}
                    step={1}
                    className="py-2"
                    data-testid="ask-threshold-slider"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>Broad (30%)</span>
                    <span>Balanced (55%)</span>
                    <span>Precise (95%)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Higher = fewer but more precise results. Lower = more results but may include loosely related questions.
                  </p>
                </div>

                {/* Save */}
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={saveTuning}
                    disabled={!tuningDirty || savingTuning}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="save-tuning-btn"
                  >
                    {savingTuning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Save Thresholds
                  </Button>
                  {tuningDirty && (
                    <button
                      onClick={() => { setTuningDraft({ ...tuning }); setTuningDirty(false); }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Reset
                    </button>
                  )}
                  {!tuningDirty && tuning.duplicate_detection_threshold === tuningDraft.duplicate_detection_threshold && (
                    <span className="text-xs text-green-600">Changes take effect immediately — no re-embedding needed.</span>
                  )}
                </div>

                {/* Model Selector */}
                {modelInfo && (
                  <div className="pt-4 mt-4 border-t" data-testid="model-selector-section">
                    <div className="flex items-center gap-2 mb-3">
                      <ArrowRightLeft className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-700">Embedding Model</p>
                    </div>
                    <div className="space-y-3">
                      <Select
                        value={modelInfo.current_model}
                        onValueChange={(val) => {
                          if (val !== modelInfo.current_model) {
                            setPendingModel(val);
                            setShowModelConfirm(true);
                          }
                        }}
                        disabled={switchingModel || isRunning}
                      >
                        <SelectTrigger className="w-full" data-testid="model-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {modelInfo.available_models?.map(m => (
                            <SelectItem key={m.key} value={m.key}>
                              <div className="flex flex-col">
                                <span className="font-medium">{m.display_name}</span>
                                <span className="text-xs text-gray-500">{m.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {switchingModel && (
                        <div className="flex items-center gap-2 text-sm text-teal-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Switching model and re-computing embeddings...
                        </div>
                      )}
                      <p className="text-xs text-gray-400">
                        Switching models deletes all embeddings and recomputes them. This takes a few minutes.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stale Embeddings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-gray-500" />
                  Freshness Check
                </CardTitle>
                <CardDescription>
                  Detect and refresh embeddings for questions whose text was edited after embedding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {staleInfo ? (
                  <>
                    <div className="flex items-center gap-3">
                      {staleInfo.stale_count === 0 ? (
                        <Badge className="bg-green-100 text-green-800" data-testid="stale-badge">
                          <CheckCircle className="w-3 h-3 mr-1" /> All Fresh
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800" data-testid="stale-badge">
                          <AlertCircle className="w-3 h-3 mr-1" /> {staleInfo.stale_count} Stale
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        out of {staleInfo.total_with_embeddings} embedded questions
                      </span>
                    </div>
                    {staleInfo.stale_count > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={refreshStale}
                        disabled={refreshing}
                        data-testid="refresh-stale-btn"
                      >
                        {refreshing ? (
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-2" />
                        )}
                        Refresh {staleInfo.stale_count} Stale Embeddings
                      </Button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Checking freshness...</p>
                )}
              </CardContent>
            </Card>

            {/* Last Job Info */}
            {status?.last_job && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="w-5 h-5 text-gray-500" />
                    Last Backfill Job
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-2">
                        {status.last_job.status === 'completed' && (
                          <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        )}
                        {status.last_job.status === 'running' && (
                          <Badge className="bg-teal-100 text-teal-800">Running</Badge>
                        )}
                        {status.last_job.status === 'failed' && (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Processed:</span>
                      <span className="ml-2 font-medium">{status.last_job.processed} / {status.last_job.total}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Started:</span>
                      <span className="ml-2">{formatDate(status.last_job.started_at)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Completed:</span>
                      <span className="ml-2">{formatDate(status.last_job.completed_at)}</span>
                    </div>
                    {status.last_job.errors > 0 && (
                      <div className="col-span-2">
                        <span className="text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {status.last_job.errors} errors during processing
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Info */}
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="pt-5 pb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">How it works</h4>
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li>Embeddings are 384-dimensional vectors that capture the semantic meaning of each question.</li>
                  <li>When checking for duplicates, questions are compared by meaning (not just text similarity).</li>
                  <li>Pre-computing embeddings makes similarity checks faster during bulk imports and reviews.</li>
                  <li>New questions are automatically embedded when approved — this page is for backfilling existing questions.</li>
                  <li>The maritime abbreviation dictionary expands terms like OWS, SOPEP, SOLAS before computing embeddings.</li>
                </ul>
              </CardContent>
            </Card>

            {/* Abbreviation Dictionary Link */}
            <Card>
              <CardContent className="py-4">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/admin/tools/abbreviations')}
                  data-testid="abbreviation-dict-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2 text-indigo-600" />
                  Abbreviation Dictionary
                  <span className="ml-auto text-xs text-gray-400">Manage maritime term expansions for better matching</span>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Model Switch Confirmation Dialog */}
      <Dialog open={showModelConfirm} onOpenChange={setShowModelConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Switch Embedding Model?
            </DialogTitle>
            <DialogDescription className="text-left">
              {pendingModel && modelInfo && (
                <>
                  Switching from <strong>{modelInfo.available_models?.find(m => m.key === modelInfo.current_model)?.display_name}</strong> to{' '}
                  <strong>{modelInfo.available_models?.find(m => m.key === pendingModel)?.display_name}</strong>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">This will:</p>
              <ul className="space-y-1 text-xs">
                <li>Delete all existing embeddings ({status?.total_embedded?.toLocaleString() || 0} vectors)</li>
                <li>Recompute embeddings for all {status?.total_approved_questions?.toLocaleString() || 0} approved questions</li>
                <li>Take a few minutes to complete</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Search and duplicate detection will use partial results during re-computation.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowModelConfirm(false); setPendingModel(null); }}>
              Cancel
            </Button>
            <Button onClick={handleModelSwitch} className="bg-amber-600 hover:bg-amber-700">
              Switch Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EmbeddingsManagementPage;
